import type { ChartConfiguration, ChartEvent, Chart as ChartJS, ChartType } from "chart.js"
declare module "chart.js" {
	interface PluginOptionsByType<TType extends ChartType> {
		crosshairV2?: CrossHairPluginProps
	}
}

type SyncEvent = CustomEvent<{
	chartId: string
	syncGroup: number | string
	original: ChartEvent
	xValue: number | undefined
	index?: number | null
	label?: string | null
	state?: "hover" | "leave"
}>

type DefaultOptions = {
	line?: {
		color: string
		width: number
		dashPattern: number[] // not sure about this type. check chartjs for references if needed.
	}
	sync?: {
		enabled: boolean
		group: number | string
		suppressTooltips: boolean
	}
	snap?: {
		enabled: boolean
	}
}
export type CrossHairPluginProps = DefaultOptions & {
	timestampGetter: (tRef: number) => void
	crosshair:
		| {
				timeout: ReturnType<typeof setTimeout> | undefined
				enabled: boolean
				suppressUpdate: boolean
				x: number | null
				suppressTooltips: boolean
				ignoreNextEvents: number
				ready: boolean
				pendingSyncEvent: SyncEvent | undefined
				syncEventHandler: ((e: SyncEvent) => void) | undefined
		  }
		| undefined
}

type MyEvent = Event & {
	x?: number | null
	y?: number | null
	native?: MyEvent | null
	stop?: boolean
	chartId?: string
}

const defaultOptions: Required<DefaultOptions> = {
	line: {
		color: "#F66",
		width: 1,
		dashPattern: [],
	},
	sync: {
		enabled: true,
		group: 1,
		suppressTooltips: false,
	},
	snap: {
		enabled: false,
	},
}

export const crosshairV2 = {
	id: "crosshairV2",
	afterInit: function (chart: ChartJS) {
		if (chart.config.options?.scales?.x === undefined) {
			return
		}

		const xScaleType = chart.config.options?.scales.x.type ?? ""

		if (["linear", "time", "category", "logarithmic"].includes(xScaleType) === false) return

		let pluginRef = chart.config.options?.plugins?.crosshairV2
		if (pluginRef === undefined) return

		if (Object.keys(pluginRef).length === 0) pluginRef = defaultOptions

		pluginRef = {
			...pluginRef,
			crosshair: {
				timeout: undefined,
				enabled: false,
				suppressUpdate: false,
				x: null,
				suppressTooltips: false,
				ignoreNextEvents: 0,
				ready: false,
				pendingSyncEvent: undefined,
			},
		}

		chart.config.options.plugins!.crosshairV2 = pluginRef

		const syncEnabled =
			chart?.config?.options?.plugins?.crosshairV2?.sync?.enabled ??
			defaultOptions.sync.enabled

		if (syncEnabled === true) {
			chart.config.options.plugins!.crosshairV2.crosshair!.syncEventHandler = (
				e: SyncEvent
			) => {
				this.handleSyncEvent(chart, e)
			}
			window.addEventListener(
				"sync-event",
				chart.config.options!.plugins!.crosshairV2!.crosshair!
					.syncEventHandler! as EventListener
			)
		}
	},
	destroy: function (chart: ChartJS) {
		const options = chart?.config?.options?.plugins?.crosshairV2
		const syncEnabled = options?.sync?.enabled ?? defaultOptions.sync.enabled
		const syncEventHandler = options?.crosshair?.syncEventHandler

		if (syncEventHandler === undefined) return

		if (syncEnabled === true)
			window.removeEventListener("sync-event", syncEventHandler as EventListener)
	},
	getXScale: function (chart: ChartJS) {
		const xAxisId = chart.getDatasetMeta(0).xAxisID
		return chart.data.datasets.length > 0 && xAxisId !== undefined
			? chart.scales[xAxisId]
			: null
	},
	getYScale: function (chart: ChartJS) {
		const yAxisId = chart.getDatasetMeta(0).yAxisID
		return yAxisId !== undefined ? chart.scales[yAxisId] : null
	},
	applyPendingSyncEvent: function (chart: ChartJS) {
		const pluginRef = chart.config.options?.plugins?.crosshairV2?.crosshair
		if (pluginRef === undefined) return

		const pendingSyncEvent = pluginRef.pendingSyncEvent
		if (pluginRef.ready === false || pendingSyncEvent === undefined) {
			return
		}

		pluginRef.pendingSyncEvent = undefined
		this.handleSyncEvent(chart, pendingSyncEvent as SyncEvent)
	},
	handleSyncEvent: function (chart: ChartJS, e: SyncEvent) {
		const options = chart?.config?.options?.plugins?.crosshairV2
		const syncGroup = options?.sync?.group ?? defaultOptions.sync.group

		// stop if the sync event was fired from this chart
		if (e.detail.chartId === chart.id) return

		// stop if the sync event was fired from a different group
		if (e.detail.syncGroup !== syncGroup) return

		// stop if sync event should not be updated
		if (options?.crosshair?.suppressUpdate === true) return

		const xScale = this.getXScale(chart)
		if (xScale === null || xScale === undefined) return

		const pluginRef = chart.config.options?.plugins?.crosshairV2?.crosshair
		if (pluginRef === undefined) return

		pluginRef.suppressTooltips =
			options?.sync?.suppressTooltips ?? defaultOptions.sync.suppressTooltips

		if (pluginRef.ready === false) {
			pluginRef.pendingSyncEvent = e
			return
		}

		const isLeave =
			e.detail.state === "leave" ||
			e.detail.index === null ||
			typeof e.detail.xValue !== "number" ||
			Number.isFinite(e.detail.xValue) === false ||
			e.detail.xValue < 0

		const clearActiveState = () => {
			pluginRef.enabled = false
			pluginRef.x = null
			pluginRef.pendingSyncEvent = undefined
			chart.setActiveElements([])
			chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
			chart.update("none")
		}

		const primeHoverState = (x: number, y: number) => {
			if (chart.canvas?.isConnected !== true) {
				return
			}

			const syntheticEvent: MyEvent = new Event("mousemove")
			syntheticEvent.x = x
			syntheticEvent.y = y
			syntheticEvent.native = {
				chartId: chart.id,
				stop: true,
			} as MyEvent
			syntheticEvent.stop = true
			syntheticEvent.chartId = chart.id
			chart.canvas.dispatchEvent(syntheticEvent)
		}

		if (isLeave === true) {
			clearActiveState()
			return
		}

		const syncLabel = e.detail.label

		if (
			xScale.type === "category" &&
			typeof syncLabel === "string" &&
			syncLabel.length > 0
		) {
			const index = chart.data.labels?.findIndex((label) => `${label}` === syncLabel) ?? -1
			if (index < 0) {
				clearActiveState()
				return
			}
			const activeElements = chart.getSortedVisibleDatasetMetas().flatMap((meta) => {
				const element = meta.data[index]
				return element === undefined ? [] : [{ datasetIndex: meta.index, index }]
			})

			if (activeElements.length === 0) {
				clearActiveState()
				return
			}

			pluginRef.enabled = true
			pluginRef.x = xScale.getPixelForValue(index)

			const firstElement = chart.getDatasetMeta(activeElements[0]?.datasetIndex ?? 0).data[
				index
			]
			const center =
				firstElement !== undefined && "x" in firstElement && "y" in firstElement
					? { x: firstElement.x, y: firstElement.y }
					: { x: chart.width / 2, y: chart.height / 2 }

			const applyActiveState = () => {
				if (chart.canvas?.isConnected !== true) {
					return
				}

				chart.setActiveElements(activeElements)
				chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
				chart.tooltip?.setActiveElements(activeElements, center)
				chart.update("none")
			}

			primeHoverState(center.x, center.y)
			applyActiveState()
			requestAnimationFrame(applyActiveState)
			return
		}

		// if e.detail.orginal is undefined, the event was sent by a custom event emitter outside a chart
		const myEvent: MyEvent = new Event(
			e.detail?.original === undefined
				? "mousemove"
				: e.detail?.original.type == "click"
					? "mousemove"
					: e.detail.original.type
		)
		myEvent.x = xScale.getPixelForValue(e.detail.xValue ?? 0)
		myEvent.y = e.detail?.original !== undefined ? e.detail.original.y : chart.canvas.height / 2
		myEvent.native = e.detail?.original !== undefined ? e.detail?.original.native : null
		myEvent.stop = true
		myEvent.chartId = e.detail.chartId

		chart.canvas.dispatchEvent(myEvent)
	},
	afterEvent: function (
		chart: ChartJS,
		args: {
			event: ChartEvent
			replay: boolean
			changed?: boolean
			cancelable: false
			inChartArea: boolean
		},
		props: CrossHairPluginProps
	) {
		const e = args.event
		if (e === null) return

		const native = e.native as MyEvent | null

		if (chart.id === native?.chartId && args.inChartArea === false) {
			// remove tooltip generated in the previous render when cursor goes outside the chart area
			const syncGroup =
				chart?.config?.options?.plugins?.crosshairV2?.sync?.group ??
				defaultOptions.sync.group

			const stopPropagation = native !== null ? native.stop : false
			if (stopPropagation === undefined || stopPropagation !== true) {
				const event = new CustomEvent("sync-event", {
					bubbles: true,
					detail: {
						chartId: native?.chartId ?? chart.id, //chart.id,
						syncGroup: syncGroup,
						original: e,
						xValue: undefined,
						index: null,
						label: null,
						state: "leave",
					},
				})
				window.dispatchEvent(event)
			}
			return
		}

		const pluginRef = chart.config.options?.plugins?.crosshairV2?.crosshair
		if (pluginRef === undefined) return true

		const xValue = e.x //Number.isNaN(e.x) !== true ? e.x : native.detail.x
		if (xValue === null) return

		const xScale = chart.scales.x
		if (xScale === undefined) return

		const xScaleType = xScale.type

		if (["linear", "time", "category", "logarithmic"].includes(xScaleType) === false) return

		if (pluginRef.ignoreNextEvents !== undefined && pluginRef.ignoreNextEvents > 0) {
			pluginRef.ignoreNextEvents -= 1
			return
		}

		const syncEnabled =
			chart?.config?.options?.plugins?.crosshairV2?.sync?.enabled ??
			defaultOptions.sync.enabled
		const syncGroup =
			chart?.config?.options?.plugins?.crosshairV2?.sync?.group ?? defaultOptions.sync.group
		const resolvedValue = xScale.getValueForPixel(xValue)

		// fire event for all other linked charts
		const stopPropagation = native !== null ? native.stop : false
		if (stopPropagation === undefined || (stopPropagation !== true && syncEnabled === true)) {
			const activeElements = chart.getActiveElements()
			const activeIndex = activeElements[0]?.index
			const activeLabel =
				typeof activeIndex === "number" && activeIndex >= 0
					? `${chart.data.labels?.[activeIndex] ?? ""}`
					: undefined

			const event = new CustomEvent("sync-event", {
				bubbles: true,
				detail: {
					chartId: native?.chartId ?? chart.id,
					syncGroup: syncGroup,
					original: e,
					xValue: resolvedValue,
					index: xScale.type === "category" ? activeIndex : undefined,
					label: xScale.type === "category" ? activeLabel : undefined,
					state: e.type === "mouseout" ? "leave" : "hover",
				},
			})

			window.dispatchEvent(event)
			if (pluginRef.timeout !== undefined) {
				clearTimeout(pluginRef.timeout as number)
			}

			if (
				props.timestampGetter !== undefined &&
				xScale !== null &&
				xValue !== null &&
				resolvedValue !== undefined
			) {
				props.timestampGetter(resolvedValue)
			}
		}

		// suppress tooltips for linked charts
		const suppressTooltips =
			chart?.config?.options?.plugins?.crosshairV2?.sync?.suppressTooltips ??
			defaultOptions.sync.suppressTooltips

		pluginRef.suppressTooltips = suppressTooltips

		pluginRef.enabled =
			e.type !== "mouseout" &&
			xValue > xScale.getPixelForValue(xScale.min) &&
			xValue < xScale.getPixelForValue(xScale.max)

		// if (!pluginRef.enabled && pluginRef.suppressUpdate === false) {
		// 	if (xValue >= xScale.getPixelForValue(xScale.max)) {
		// 		// suppress future updates to prevent endless redrawing of chart
		// 		pluginRef.suppressUpdate = true
		// 		// chart.update("none") //this line may add stack overflow bug, be aware!!
		// 	}

		// 	return false
		// }
		// pluginRef.suppressUpdate = false
		pluginRef.x = xValue
		return
	},
	beforeDatasetsDraw(chart: ChartJS) {
		const chartType = (chart.config as ChartConfiguration)?.type
		if (chartType !== null && chartType === "bar") {
			this.drawBackgroundHighlight(chart)
		}
	},
	afterDraw: function (chart: ChartJS) {
		// const pluginRef = chart.config.options?.plugins?.crosshairV2?.crosshair
		// if (pluginRef === undefined) {
		// 	return
		// }
		// if (pluginRef.enabled === undefined) {
		// 	return
		// }
		// if (pluginRef.enabled === false) {
		// 	return
		// }
		this.drawTraceLine(chart)
		// return false
	},
	afterRender: function (chart: ChartJS) {
		const pluginRef = chart.config.options?.plugins?.crosshairV2?.crosshair
		if (pluginRef === undefined) return

		pluginRef.ready = true
		this.applyPendingSyncEvent(chart)
	},
	beforeTooltipDraw: function (chart: ChartJS) {
		const pluginRef = chart.config.options?.plugins?.crosshairV2?.crosshair
		if (pluginRef === undefined) return true

		if (pluginRef.suppressUpdate === true) return false

		return pluginRef.suppressTooltips !== true
	},
	drawBackgroundHighlight: function (chart: ChartJS) {
		const options = chart.config.options?.plugins?.crosshairV2

		const pluginRef = options?.crosshair
		if (pluginRef === undefined) {
			return
		}
		const yScale = this.getYScale(chart)

		if (yScale === null || yScale === undefined) {
			return
		}
		const ctx = chart.ctx
		ctx.save()

		const activeElements = chart.getActiveElements()
		if (activeElements.length === 0) {
			ctx.restore()
			return
		}

		let minX = Infinity,
			maxX = -Infinity
		const topY = yScale.getPixelForValue(yScale.max)
		const bottomY = yScale.getPixelForValue(yScale.min)

		activeElements.forEach(({ element }) => {
			if (element === undefined || element === null) return
			const { width, x } = element.getProps(["width", "x"])
			if (width === undefined) return
			minX = Math.min(minX, x - width / 2)
			maxX = Math.max(maxX, x + width / 2)
		})

		ctx.fillStyle = "#2b4861"
		ctx.globalAlpha = 0.15
		ctx.fillRect(
			minX - (maxX - minX) / 12.5,
			topY,
			maxX - minX + (2 * (maxX - minX)) / 12.5,
			bottomY - topY
		)
		ctx.globalAlpha = 1
	},
	drawTraceLine: function (chart: ChartJS) {
		const options = chart.config.options?.plugins?.crosshairV2
		const pluginRef = options?.crosshair
		if (pluginRef === undefined) {
			return
		}
		const yScale = this.getYScale(chart)

		if (yScale === null || yScale === undefined) {
			return
		}
		const color = options?.line?.color ?? defaultOptions.line.color
		const snapEnabled = options?.snap?.enabled ?? defaultOptions.snap.enabled
		const chartType = (chart.config as ChartConfiguration)?.type

		const ctx = chart.ctx
		ctx.save()

		const activeElements = chart.getActiveElements()
		if (activeElements.length === 0) {
			ctx.restore()
			return
		}

		if (chartType !== null && chartType === "bar") {
			return
		}
		const lineWidth = options?.line?.width ?? defaultOptions.line.width
		if (lineWidth === 0) return

		const dashPattern = (options?.line?.dashPattern ??
			defaultOptions.line.dashPattern) as number[]

		let lineX = pluginRef.x

		if (snapEnabled === true && chart.getActiveElements().length > 0) {
			lineX = chart.getActiveElements()[0]?.element.x
		}
		if (lineX === undefined || lineX === null) {
			return
		}
		if (
			chart.isPointInArea({
				x: lineX,
				y: (yScale.getPixelForValue(yScale.max) + yScale.getPixelForValue(yScale.min)) / 2,
			}) === false
		) {
			return
		}

		chart.ctx.beginPath()
		chart.ctx.setLineDash(dashPattern)
		chart.ctx.moveTo(lineX, yScale.getPixelForValue(yScale.max))
		chart.ctx.lineWidth = lineWidth
		chart.ctx.strokeStyle = color
		chart.ctx.lineTo(lineX, yScale.getPixelForValue(yScale.min))
		chart.ctx.stroke()
		chart.ctx.setLineDash([])

		ctx.restore()
	},
}
